import React, { useMemo } from "react";
import { observer } from "mobx-react";
import { Alert, Checkbox } from "antd";
import List from "@app/components/list/ordered";
import Loader from "@app/components/loader/content";

import FieldList from "./field-list";
import SourceDocsPicker from "./source-docs-picker";
import ArticlePicker from "./article-picker";
import CopyFrom from "./copy-from";

import "./style.scoped.scss";

// Move one item within a list, or null when the move is out of bounds.
const withMoved = (list, { from, to }) => {
    if (from < 0 || from >= list.length || to < 0 || to >= list.length) {
        return null;
    }
    const next = [...list];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
};

// Keep `prior`'s ordering for everything still in `scope`, then append whatever
// entered the scope since.
const rankedWithin = (prior, scope) => [
    ...prior.filter((id) => scope.includes(id)),
    ...scope.filter((id) => !prior.includes(id)),
];

/**
 * One generated section: the fields it needs, the inputs it searches, and — for
 * the document-scoped sections — the priority between those inputs.
 *
 * A section is scoped either by source documents or by the project's screened
 * literature, never both (the template's `sectionArticleType`). Five form keys,
 * all kept in `_id` space (the backend translates ids to filenames when building
 * the run):
 *   - section_documents        : { [key]: [docId] }      the document scope
 *   - section_priority         : { [key]: [docId] }      ordered tie-breaker
 *   - section_priority_enabled : { [key]: boolean }      whether priority is used
 *   - section_articles         : { [key]: [articleId] }  the article scope
 *
 * `articles` is `{ slr, sota }`, each channel null until its fetch resolves.
 */
const SectionPanel = observer(
    ({
        template,
        sectionKey,
        values,
        setValue,
        documents,
        articles,
        selectedSections,
        onCopyFrom,
    }) => {
        const nameById = useMemo(() => {
            const map = new Map();
            (documents || []).forEach((d) => map.set(d._id, d.name));
            return map;
        }, [documents]);

        const entry = template.sectionCatalog.find((s) => s.key === sectionKey);
        const sectionLabel = entry?.label || sectionKey;
        const articleType = template.sectionArticleType[sectionKey];
        const channel = articleType ? (articles || {})[articleType] : null;
        const articleById = useMemo(() => {
            const map = new Map();
            (channel || []).forEach((a) => map.set(a._id, a));
            return map;
        }, [channel]);

        const sectionDocuments = values.section_documents || {};
        const sectionPriority = values.section_priority || {};
        const priorityEnabled = values.section_priority_enabled || {};
        const sectionArticles = values.section_articles || {};

        const takesDocuments = !template.sectionsWithoutDocuments.has(sectionKey);
        const takesArticles = Boolean(articleType);
        // Takes documents, but may be left with none: the run is still accepted and
        // the document loses something instead. Distinguishing the two is what
        // keeps the empty-list notice honest.
        const optional = Boolean(template.optionalSections?.has(sectionKey));

        // Each list is null until its fetch resolves. Don't prune the stored
        // assignments against it before then, or they all look unassigned.
        const loaded = documents !== null && documents !== undefined;
        const articlesLoaded = channel !== null && channel !== undefined;

        const assigned = useMemo(() => {
            const ids = Array.isArray(sectionDocuments[sectionKey])
                ? sectionDocuments[sectionKey]
                : [];
            return loaded ? ids.filter((id) => nameById.has(id)) : ids;
        }, [sectionDocuments, sectionKey, nameById, loaded]);

        const assignedArticles = useMemo(() => {
            const ids = Array.isArray(sectionArticles[sectionKey])
                ? sectionArticles[sectionKey]
                : [];
            return articlesLoaded ? ids.filter((id) => articleById.has(id)) : ids;
        }, [sectionArticles, sectionKey, articleById, articlesLoaded]);

        // Keep the priority list a live subset of the assigned files.
        const setAssigned = (nextDocs) => {
            setValue("section_documents", { ...sectionDocuments, [sectionKey]: nextDocs });

            const currentPri = Array.isArray(sectionPriority[sectionKey])
                ? sectionPriority[sectionKey]
                : [];
            if (!priorityEnabled[sectionKey]) {
                // Still prune, so a stale ranking can't resurface when priority is
                // switched back on.
                const pruned = currentPri.filter((id) => nextDocs.includes(id));
                if (pruned.length !== currentPri.length) {
                    setValue("section_priority", { ...sectionPriority, [sectionKey]: pruned });
                }
                return;
            }
            setValue("section_priority", {
                ...sectionPriority,
                [sectionKey]: rankedWithin(currentPri, nextDocs),
            });
        };

        const setAssignedArticles = (nextArticles) => {
            setValue("section_articles", { ...sectionArticles, [sectionKey]: nextArticles });
        };

        const togglePriority = () => {
            const enabled = !priorityEnabled[sectionKey];
            setValue("section_priority_enabled", { ...priorityEnabled, [sectionKey]: enabled });
            if (enabled) {
                const prior = Array.isArray(sectionPriority[sectionKey])
                    ? sectionPriority[sectionKey]
                    : [];
                setValue("section_priority", {
                    ...sectionPriority,
                    [sectionKey]: rankedWithin(prior, assigned),
                });
            }
        };

        const ranking = priorityEnabled[sectionKey]
            ? (sectionPriority[sectionKey] || []).filter((id) => assigned.includes(id))
            : [];
        const covered = ranking.length === assigned.length;

        const reorderArticles = (move) => {
            const next = withMoved(assignedArticles, move);
            if (next) {
                setValue("section_articles", { ...sectionArticles, [sectionKey]: next });
            }
        };

        const reorderRanking = (move) => {
            const next = withMoved(ranking, move);
            if (next) {
                setValue("section_priority", { ...sectionPriority, [sectionKey]: next });
            }
        };

        const orderedList = (ids, onMove, labelOf) => (
            <List id={`order-${sectionKey}`} onChange={onMove}>
                {ids.map((id, index) => (
                    <List.Item key={id} id={id} index={index}>
                        <div className="priority-item">
                            <span className="rank">{index + 1}</span>
                            <span className="name">{labelOf(id)}</span>
                        </div>
                    </List.Item>
                ))}
            </List>
        );

        const articleLabel = (id) => {
            const article = articleById.get(id);
            return article ? `${article.cid} ${article.title || ""}`.trim() : id;
        };

        return (
            <div className="cer-section-panel">
                {onCopyFrom && (
                    <div className="section-actions">
                        <CopyFrom
                            template={template}
                            sectionKey={sectionKey}
                            onApply={onCopyFrom}
                        />
                    </div>
                )}

                {(template.advisories?.(sectionKey, values) || []).map((a, i) => (
                    <Alert key={i} className="advisory" showIcon {...a} />
                ))}

                <FieldList
                    template={template}
                    section={sectionKey}
                    values={values}
                    setValue={setValue}
                    selectedSections={selectedSections}
                />

                {/* A panel that deviates from *fields → documents → priority* says
                    so where the missing control would have been: silently omitting
                    one reads as an oversight rather than as the contract. */}
                {!takesDocuments && (
                    <div className="cer-field section-note">
                        <label>Documents for this section</label>
                        <div className="field-description">
                            {takesArticles
                                ? "None. This section is scoped by the project's screened literature rather than by source documents — pick its articles below."
                                : "None. This section is configured entirely from the fields above — it reads no source documents, so there is nothing to assign or prioritise."}
                        </div>
                    </div>
                )}

                {takesDocuments && (
                    <div className="cer-field">
                        <label>Documents for this section</label>
                        <div className="field-description">
                            {entry?.description ||
                                "The files this section is allowed to read. All of them are searched co-equally unless a priority order is set below."}
                        </div>
                        {loaded ? (
                            <SourceDocsPicker
                                documents={documents}
                                value={assigned}
                                onChange={setAssigned}
                            />
                        ) : (
                            <Loader loading={true} />
                        )}
                    </div>
                )}

                {takesDocuments && loaded && assigned.length === 0 && optional && (
                    <Alert
                        className="advisory"
                        type="info"
                        showIcon
                        message="This section can be left empty"
                        // Says only what the picker's own description above does
                        // not: that the run still goes ahead. Warning a user about
                        // something the adapter accepts is how a notice stops being
                        // read at all.
                        description={`Nothing is assigned to "${sectionLabel}", which does not stop the run — generation goes ahead without it.`}
                    />
                )}

                {takesDocuments && loaded && assigned.length === 0 && !optional && (
                    <Alert
                        className="advisory"
                        type="warning"
                        showIcon
                        message="This section needs at least one file"
                        description={
                            // A section the user cannot deselect has only one way
                            // out, so don't offer the other.
                            template.fixedSections
                                ? `A step with nothing assigned to it is rejected before generation starts, and "${sectionLabel}" is always generated. Assign at least one file.`
                                : `A requested section with nothing assigned to it is rejected before generation starts. Assign a file, or deselect "${sectionLabel}" in the previous step.`
                        }
                    />
                )}

                {takesArticles && (
                    <div className="cer-field">
                        <label>Articles for this section</label>
                        <div className="field-description">
                            {sectionKey === "sota"
                                ? "The screened SoTA literature to send with this run. Section 4 has no per-document scoping — it reads every file in the run — so this decides which articles are uploaded rather than which ones section 4 may look at."
                                : "The screened DUE literature this section is built from. Only L2-included articles are listed, and only those with a retrieved PDF can be sent."}
                        </div>
                        {articlesLoaded ? (
                            <ArticlePicker
                                articles={channel}
                                value={assignedArticles}
                                onChange={setAssignedArticles}
                            />
                        ) : (
                            <Loader loading={true} />
                        )}
                    </div>
                )}

                {takesArticles && articlesLoaded && assignedArticles.length === 0 && (
                    <Alert
                        className="advisory"
                        type="warning"
                        showIcon
                        message="This section needs at least one article"
                        description={`A requested section with nothing assigned to it is rejected before generation starts. Assign an article, or deselect "${sectionLabel}" in the previous step.`}
                    />
                )}

                {/* Literature is the one section whose *assignment* order is
                    load-bearing: the order of its articles drives the numbering in
                    §6.2.1. So it orders the assignment itself rather than carrying
                    a priority list beside it. */}
                {takesArticles && articlesLoaded && sectionKey === template.orderedSectionKey && (
                    <div className="cer-field">
                        <label>Article order</label>
                        <div className="field-description">
                            Always applied — this section has no optional priority setting, because
                            its order is not a tie-breaker: it is the order the articles are
                            numbered in in the generated section. Drag to reorder.
                        </div>
                        {assignedArticles.length > 0 ? (
                            orderedList(assignedArticles, reorderArticles, articleLabel)
                        ) : (
                            <div className="list-empty">
                                Assign articles to this section to order them.
                            </div>
                        )}
                    </div>
                )}

                {takesDocuments && (
                    <div className="cer-field priority-control">
                        <Checkbox checked={!!priorityEnabled[sectionKey]} onChange={togglePriority}>
                            Set a priority order between this section&apos;s files
                        </Checkbox>
                        <div className="field-description">
                            A tie-breaker: when the same fact appears in more than one file, the
                            earliest-ranked wins.
                        </div>
                        {priorityEnabled[sectionKey] &&
                            (assigned.length === 0 ? (
                                <div className="priority-empty">
                                    Assign files to this section to rank them.
                                </div>
                            ) : (
                                <>
                                    {/* A ranking the engine can see is authoritative:
                                        files it omits are excluded from the section
                                        rather than ranked last. So a partial ranking
                                        is dropped on save — say so here rather than
                                        let it look like it took effect. */}
                                    <div
                                        className={`priority-coverage${covered ? "" : " partial"}`}
                                    >
                                        {covered
                                            ? `Ranks all ${assigned.length} assigned file(s).`
                                            : `Ranks ${ranking.length} of ${assigned.length} assigned file(s) — a partial ranking would exclude the rest from this section, so it is dropped and the files are searched co-equally.`}
                                    </div>
                                    {orderedList(
                                        ranking,
                                        reorderRanking,
                                        (id) => nameById.get(id) || id,
                                    )}
                                </>
                            ))}
                    </div>
                )}
            </div>
        );
    },
);

export default SectionPanel;
